import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listen, platform } from "./tauri-web";
import { openFileDialog, saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import { PhotoButton } from "./chat/photo";
import { LectureRecorder } from "./lecture";
import { PhotoViewer, type ViewerPhoto } from "./PhotoViewer";
import {
  store, relativeDays, dedupeStrings, todayLocal, formatMinutes, parseArtifactBody,
  KIND_LABELS, LEVELS,
  type Subject, type Material, type MaterialPhoto, type Artifact, type ArtifactKind, type StudySession,
  type StudyNotes, type Explanation, type StudyPlan, type Level, type ArtifactBody,
} from "./store";
import {
  digestMaterial, explainConcept, buildPlan, openWebLogin, academicRefusal,
  BrainError, type BrainErrorCode,
} from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { buildExport } from "./export";
import { BackupMenu } from "./BackupMenu";
import { MicButton, SpeakButton } from "./chat/voice";
import Chat from "./chat/Chat";
import {
  Sparkles, Trash2, X, Lock, BookOpen, WifiOff, RefreshCw, Camera, Clock,
  AlertCircle, Plus, Copy, Check, Coins, GraduationCap, Lightbulb, Calendar,
  FileText, Pencil, ClipboardType, ArrowLeft,
} from "lucide-react";

// Sim/screenshot rig: 1 = bypass consent+login so shot runs can reach every screen.
// ⚠️ MUST be 0 in any shipped build (per-app ledger checks this).
const SCREENSHOT = 0;

// ── "The private tutor's desk" (2026-08-05 redesign) ──
// Warmth is the only ambient signal: a desk holds the heat of its last session.
import glyphNotes from "./assets/glyph-study-notes.svg";
import glyphExplanation from "./assets/glyph-explanation.svg";
import glyphPlan from "./assets/glyph-plan.svg";
import glyphMaterial from "./assets/glyph-material-page.svg";
import glyphSelfCheck from "./assets/glyph-self-check.svg";
import glyphClock from "./assets/glyph-session-clock.svg";
import momentEmptyDesk from "./assets/moment-empty-desk.svg";
import momentFirstArtifact from "./assets/moment-first-artifact.svg";

const KIND_GLYPH: Record<string, string> = { notes: glyphNotes, explanation: glyphExplanation, plan: glyphPlan };

function daysSinceIso(iso: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? Math.floor((Date.now() - t) / 86400000) : null;
}
// warm-0 today/yesterday · 1 = 2-6d · 2 = 7-13d · 3 = 14d+/never
function warmthBand(days: number | null): 0 | 1 | 2 | 3 {
  if (days === null) return 3;
  if (days <= 1) return 0;
  if (days <= 6) return 1;
  if (days <= 13) return 2;
  return 3;
}
function gapCopy(s: Subject): { text: string; cls: string } {
  const days = daysSinceIso(s.last_studied);
  if (days === null) return s.material_count > 0
    ? { text: "not studied yet", cls: " never" }
    : { text: "nothing on it yet", cls: "" };
  if (days === 0) return { text: "today", cls: " fresh" };
  if (days === 1) return { text: "yesterday", cls: "" };
  return { text: `${days} days ago`, cls: "" };
}

type HomeView =
  | { t: "home" }
  | { t: "desk"; id: string }
  | { t: "read"; id: string }
  | { t: "intake"; subjectId?: string; initialText?: string; initialTitle?: string; initialTopic?: string; materialId?: string };

/**
 * An open book. Replaces the lucide `Sparkles` this app shared with the rest of the fleet — and
 * the control's outline moved with it, because Study, Plant Diary, Closet and Speaking Coach were
 * all rendering the SAME floating circle: 54px, 999px, --accent, byte for byte (Guideline 4.3(a)).
 */
function BookMark({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 6.6C10.4 5.3 8.4 4.7 5.2 4.7v12.6c3.2 0 5.2.6 6.8 1.9 1.6-1.3 3.6-1.9 6.8-1.9V4.7c-3.2 0-5.2.6-6.8 1.9z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M12 6.6v12.6" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2 #62, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account,
  // came back, still signed in").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns study-companion://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // study-companion://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  if (SCREENSHOT) return <Home email="you@oriora.app" onSignedOut={() => {}} />;
  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented) {
    return <AiConsent brand="Oriora Study"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted) return <LoadingGate />;
  if (!authed) return <Login />;
  return <Home email={email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function LoadingGate() {
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "1.5rem" }}>
      <div style={{ textAlign: "center" }}>
        <div className="empty-art" style={{ marginBottom: "0.9rem" }}><GraduationCap size={34} /></div>
        <div className="muted row" style={{ justifyContent: "center", gap: "0.4rem", fontSize: "var(--fs-sm)" }}>
          <Sparkles size={13} className="pulse-soft" style={{ color: "var(--accent)" }} /> Opening your library…
        </div>
      </div>
    </main>
  );
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card fade-up empty-glow" style={{ width: "100%", maxWidth: 380, padding: "2.25rem 2rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
        <div className="empty-art" style={{ marginBottom: "0.9rem" }}><GraduationCap size={34} /></div>
        <div className="eyebrow" style={{ justifyContent: "center", marginBottom: "0.35rem" }}>Oriora Study</div>
        <h1 className="serif" style={{ fontSize: "1.6rem", margin: "0 0 0.5rem" }}>Sign in with Oriora</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.6, marginBottom: "1.4rem" }}>
          Uses your Oriora account — opens to sign in, then returns here. Your materials and notes
          live on your device unless you back them up or send them; the text you submit for each
          Digest, Explain, or Plan passes through your Oriora account, which doesn't retain it. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary btn-pill" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try {
              const p = platform();
              if (p === "ios") await signInIos("study-companion");
              else if (p === "macos") await signInMac("study-companion");
              // Web: same-origin login. Go to the site login and return to this app's path; the
              // Supabase session is shared on the orioralabs.com origin, so we come back signed in.
              else if (p === "web") window.location.href = "/login?next=/study-companion/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? (<><Sparkles size={14} className="pulse-soft" /> Signing in…</>) : "Sign in with Oriora"}
        </button>
        <div className="muted row" style={{ justifyContent: "center", gap: "0.3rem", fontSize: "var(--fs-xxs)", marginTop: "1rem" }}>
          <Lock size={11} /> Your study library lives on this device — you choose what's sent or backed up
        </div>
      </div>
    </main>
  );
}

type ActionModal = "digest" | "explain" | "plan" | null;

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [sessions, setSessions] = useState<StudySession[]>([]);
  const [view, setView] = useState<HomeView>({ t: "home" });
  const [justLogged, setJustLogged] = useState<string | null>(null);
  const [firstArtifactMoment, setFirstArtifactMoment] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<ArtifactKind | "all">("all");
  const [action, setAction] = useState<ActionModal>(null);
  // "Go deeper" on a concept hands it to Explain, prefilled — the artifact stays a living thing.
  const [deeper, setDeeper] = useState<string | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<Artifact | null>(null);
  const [manageSubjects, setManageSubjects] = useState(false);
  const [logSession, setLogSession] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // CSV export uses the desktop save-file dialog, which doesn't exist on iOS — hide the
  // affordance there. Default to showing it if platform() can't be resolved, correct after mount.
  const [isDesktop, setIsDesktop] = useState(true);
  useEffect(() => { try { setIsDesktop(platform() !== "ios" && platform() !== "android"); } catch { /* keep default */ } }, []);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const exportRef = useRef<(() => void) | null>(null);

  const reload = useCallback(async () => {
    const [subs, arts, sess] = await Promise.all([
      store.listSubjects(),
      store.listArtifacts(),
      store.listSessions({ limit: 200 }),
    ]);
    setSubjects(subs);
    setArtifacts(arts);
    setSessions(sess);
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // Native-menu actions (Actions menu in the macOS menu bar): Digest / Explain / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "digest": setView({ t: "intake" }); break;
        case "explain": setAction("explain"); break;
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const subjectById = useMemo(() => new Map(subjects.map((s) => [s.id, s])), [subjects]);

  const totalMinutes = useMemo(() => subjects.reduce((n, s) => n + s.total_minutes, 0), [subjects]);
  const totalSessions = useMemo(() => subjects.reduce((n, s) => n + s.session_count, 0), [subjects]);

  const shownArtifacts = useMemo(() => artifacts.filter((a) =>
    kindFilter === "all" || a.kind === kindFilter), [artifacts, kindFilter]);
  const shownSessions = useMemo(() => sessions.slice(0, 6), [sessions]);

  const doExport = useCallback(() => {
    if (artifacts.length === 0 && sessions.length === 0) return;
    const { csv, filename } = buildExport(artifacts, sessions, subjectById);
    void (async () => { const p = await saveFileDialog({ defaultPath: filename }); if (p) await writeTextFile(p, csv); })();
  }, [artifacts, sessions, subjectById]);
  exportRef.current = doExport;

  async function doDeleteArtifact(id: string) {
    setConfirmDelete(null);
    await store.deleteArtifact(id);
    await reload();
  }

  const hasSubjects = subjects.length > 0;
  const defaultSubjectId = (view.t === "desk" ? view.id : undefined) ?? subjects[0]?.id ?? "";

  const openArtifact = (a: Artifact) => setView({ t: "read", id: a.id });
  const deskSubject = view.t === "desk" ? subjects.find((x) => x.id === view.id) : undefined;
  const readArtifact = view.t === "read" ? artifacts.find((x) => x.id === view.id) : undefined;

  const sharedOverlays = (
    <>
      {action === "explain" && (
        <ExplainModal subjects={subjects} defaultSubjectId={defaultSubjectId} offline={offline} initialConcept={deeper}
          onClose={() => { setAction(null); setDeeper(undefined); }}
          onSaved={() => { setAction(null); setDeeper(undefined); void reload(); }} />
      )}
      {action === "plan" && (
        <PlanModal subjects={subjects} defaultSubjectId={defaultSubjectId} offline={offline}
          onClose={() => setAction(null)} onSaved={() => { setAction(null); void reload(); }} />
      )}
      {logSession && (
        <RitualSheet subjects={subjects} defaultSubjectId={defaultSubjectId}
          onClose={() => setLogSession(false)}
          onSaved={(sid) => {
            setLogSession(false); void reload();
            setJustLogged(sid); setTimeout(() => setJustLogged((c) => (c === sid ? null : c)), 1200);
          }} />
      )}
      {manageSubjects && (
        <SubjectManagerModal subjects={subjects} onClose={() => setManageSubjects(false)} onChanged={() => { void reload(); }} />
      )}
      {confirmDelete && (
        <ModalShell title={confirmDelete.kind === "notes" ? "Delete these notes?" : `Delete this ${KIND_LABELS[confirmDelete.kind].toLowerCase()}?`} onCancel={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.25rem", lineHeight: 1.55 }}>
            {confirmDelete.title ? `“${confirmDelete.title}” leaves` : "It leaves"} the {subjectById.get(confirmDelete.subject_id)?.name ?? ""} desk for good. This can't be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn-ghost" onClick={() => setConfirmDelete(null)}>Keep it</button>
            <button className="btn btn-danger" onClick={() => { if (view.t === "read") setView({ t: "home" }); void doDeleteArtifact(confirmDelete.id); }}>Delete</button>
          </div>
        </ModalShell>
      )}
      {!chatOpen && (
        <button className="chat-bubble" aria-label="Open chat — ask, log time, or find something" onClick={() => setChatOpen(true)}>
          <BookMark size={22} />
        </button>
      )}
      <aside className={`rbc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="rbc-head">
          <span>Ask</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </>
  );

  // ── INTAKE — the front door, a full page (R2) ──
  if (view.t === "intake") {
    return (
      <>
        <IntakePage subjects={subjects} defaultSubjectId={view.subjectId ?? defaultSubjectId} offline={offline}
          initialText={view.initialText} initialTitle={view.initialTitle} initialTopic={view.initialTopic} materialId={view.materialId}
          onExplain={() => setAction("explain")} onPlan={() => setAction("plan")}
          onClose={() => setView(view.subjectId ? { t: "desk", id: view.subjectId } : { t: "home" })}
          onSaved={(subjectId, firstEver) => {
            void reload();
            if (firstEver) { setFirstArtifactMoment(subjectId); setTimeout(() => setFirstArtifactMoment(null), 4000); }
            setView({ t: "desk", id: subjectId });
          }} />
        {sharedOverlays}
      </>
    );
  }

  // ── READING PAGE (R3) ──
  if (view.t === "read" && readArtifact) {
    return (
      <>
        <ReadPage a={readArtifact} subject={subjectById.get(readArtifact.subject_id)?.name ?? ""}
          onBack={() => setView({ t: "desk", id: readArtifact.subject_id })}
          onDelete={() => setConfirmDelete(readArtifact)}
          onGoDeeper={(term) => { setDeeper(term); setAction("explain"); }}
          onLog={() => setLogSession(true)} />
        {sharedOverlays}
      </>
    );
  }

  // ── SUBJECT DESK (R1) ──
  if (view.t === "desk" && deskSubject) {
    return (
      <>
        <DeskPage subject={deskSubject}
          artifacts={artifacts.filter((a) => a.subject_id === deskSubject.id)}
          sessions={sessions.filter((x) => x.subject_id === deskSubject.id).slice(0, 6)}
          firstArtifactMoment={firstArtifactMoment === deskSubject.id}
          onBack={() => setView({ t: "home" })}
          onAddMaterial={() => setView({ t: "intake", subjectId: deskSubject.id })}
          onDigestMaterial={(m) => setView({ t: "intake", subjectId: deskSubject.id, initialText: m.content, initialTitle: m.title, initialTopic: m.topic, materialId: m.id })}
          onExplain={() => setAction("explain")} onPlan={() => setAction("plan")}
          onLog={() => setLogSession(true)}
          onOpenArtifact={openArtifact}
          onDeleteSession={(id) => { void store.deleteSession(id).then(reload); }}
          onAskLine={() => setChatOpen(true)} />
        {sharedOverlays}
      </>
    );
  }

  // ── HOME — the desks ──
  return (
    <main className="page">
      <header className="row" style={{ justifyContent: "space-between", marginBottom: "1.15rem", flexWrap: "nowrap" }}>
        <span className="wordmark">Study</span>
        <div className="row" style={{ gap: "0.35rem", alignItems: "center", flexWrap: "nowrap" }}>
          <BackupMenu
            appName="Study" fileSlug="oriora-study" appScheme="study-companion"
            hasData={artifacts.length > 0 || sessions.length > 0 || subjects.length > 0} onRestored={reload}
            onExportCsv={isDesktop && (artifacts.length > 0 || sessions.length > 0) ? doExport : undefined}
          />
          <AccountMenu email={email} appSlug="study-companion" appName="Oriora Study" onSignedOut={onSignedOut} />
          <AddFundsMenu />
        </div>
      </header>

      {offline && (
        <div role="status" className="notice notice-warn fade-up" style={{ marginBottom: "1rem" }}>
          <WifiOff size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Digest, Explain, and Plan are paused while you're offline — your library still saves on this device.</span>
        </div>
      )}

      {!hasSubjects ? (
        <div className="fade-up" style={{ textAlign: "center", padding: "3rem 0 2rem" }}>
          <div className="empty-art"><img src={momentEmptyDesk} alt="" style={{ width: 132 }} /></div>
          <h2 className="serif" style={{ fontSize: "1.6rem", margin: "0.4rem 0 0.5rem" }}>The desk is clear.</h2>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.6, maxWidth: 300, margin: "0 auto 1.4rem" }}>
            Put something on it — a passage you're stuck on, a photo of the page you're revising. Notes, explanations and plans build up around it.
          </p>
          <div style={{ display: "grid", gap: "0.5rem", maxWidth: 320, margin: "0 auto" }}>
            <button className="btn btn-primary" onClick={() => setView({ t: "intake" })}><ClipboardType size={16} /> Paste text</button>
            <button className="btn" onClick={() => setView({ t: "intake" })}><Camera size={15} /> Photograph a page</button>
          </div>
          <p className="intake-privacy" style={{ marginTop: "0.8rem" }}>Read on your device — the photo is never uploaded.</p>

          {/* READY-STATE R1 — what the desk GIVES BACK, on the first screen.
              These three are the app's BETTER rows (PARITY-MATRIX "## BETTER (ours already — say it
              in the copy)"): Layered Explain, the week-by-week Plan, and the per-call wallet. They
              were already written, in these exact words, on the verb buttons at ~:1142 — but those
              live in a sheet you can only open AFTER a material exists, so at zero data a new user
              saw two doors and no idea what comes through them (ready-state fault shapes 2 and 7:
              present, correct, and unreachable from the first screen).
              Nothing new is computed or fetched here: same strings, same glyphs, and each row is
              one tap to the SAME intake door the buttons above open. */}
          <div className="desk-gives">
            <p className="desk-gives-head">What comes back</p>
            {/* The cost sits HERE, above the list, not under it: the shared chat launcher is
                pinned bottom-right and covered this line in the first after-shot. A cost cue a
                floating button can sit on top of is not a cost cue. */}
            <p className="desk-gives-cost">One call each, from your Oriora wallet — no subscription.</p>
            {[
              { g: glyphNotes, name: "Notes from what you bring", sub: "Summary and the key concepts" },
              { g: glyphExplanation, name: "Explain one concept from it", sub: "Analogy, definition, example, the trap" },
              { g: glyphPlan, name: "Plan the run-up to a deadline", sub: "Weeks, milestones, review points" },
            ].map((v) => (
              <button key={v.name} className="desk-gives-row" onClick={() => setView({ t: "intake" })}>
                <img src={v.g} alt="" />
                <span>
                  <span className="verb-name">{v.name}</span>
                  <span className="verb-sub">{v.sub}</span>
                </span>
              </button>
            ))}
          </div>

          <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "2rem 0 0.6rem" }}>Or name a subject first, and fill it later.</p>
          <div style={{ maxWidth: 320, margin: "0 auto" }}><AddSubjectInline onAdded={reload} autoFocus={false} /></div>
          <button className="asker" style={{ marginTop: "1.5rem", maxWidth: 360, marginInline: "auto" }} onClick={() => setChatOpen(true)}>
            <Sparkles size={15} /><span>Ask, log time, or find something…</span>
          </button>
        </div>
      ) : (
        <>
          {/* R2 — intake leads */}
          <section className="intake fade-up">
            <div style={{ display: "flex", gap: "0.8rem", alignItems: "flex-start" }}>
              <img src={glyphMaterial} alt="" style={{ width: 26, flexShrink: 0, marginTop: 3 }} />
              <div style={{ minWidth: 0 }}>
                <h2 className="intake-title serif" style={{ margin: 0 }}>Bring something to study</h2>
                <div className="intake-sub">A passage, your lecture notes, a page of the textbook.</div>
              </div>
            </div>
            <div className="intake-actions">
              <button className="btn btn-primary" onClick={() => setView({ t: "intake" })}><ClipboardType size={16} /> Paste text</button>
              <button className="btn" onClick={() => setView({ t: "intake" })}><Camera size={15} /> Photograph a page</button>
            </div>
            <div className="intake-privacy">Read on your device — the photo is never uploaded.</div>
          </section>

          {/* R1 — the desks */}
          <div className="sec-head"><h2>Your desks</h2>
            <button className="link-quiet" onClick={() => setManageSubjects(true)}>Manage</button>
          </div>
          <div className="desk-grid">
            {subjects.map((sub) => {
              const band = warmthBand(daysSinceIso(sub.last_studied));
              const gap = gapCopy(sub);
              const kinds = [...new Set(artifacts.filter((a) => a.subject_id === sub.id).map((a) => a.kind))];
              return (
                <button key={sub.id} className={`desk desk-warm-${band}${justLogged === sub.id ? " just-logged" : ""}`}
                  onClick={() => setView({ t: "desk", id: sub.id })}>
                  <span className="desk-head">
                    <span className="desk-name">{sub.name}</span>
                    <span className={`desk-gap${justLogged === sub.id ? " fresh" : gap.cls}`}>{justLogged === sub.id ? "just now" : gap.text}</span>
                  </span>
                  <span className="desk-meta">
                    <span>{sub.material_count} material{sub.material_count === 1 ? "" : "s"}{sub.artifact_count > 0 ? ` · ${sub.artifact_count} saved` : ""}</span>
                    <span className="desk-kinds">{kinds.map((k) => <img key={k} src={KIND_GLYPH[k]} alt={k} />)}</span>
                  </span>
                </button>
              );
            })}
            <AddDeskButton onAdded={reload} />
          </div>

          {/* Library */}
          {artifacts.length > 0 && (
            <>
              <div className="sec-head"><h2>Everything you've made <span className="muted tnum" style={{ letterSpacing: 0, textTransform: "none" }}>({artifacts.length})</span></h2></div>
              <div className="row" style={{ gap: "0.3rem", marginBottom: "0.5rem" }}>
                {(["all", "notes", "explanation", "plan"] as const).map((k) => (
                  <button key={k} className={`chip chip-mini toggle${kindFilter === k ? " selected" : ""}`} onClick={() => setKindFilter(k)}>
                    {k === "all" ? "All" : KIND_LABELS[k]}
                  </button>
                ))}
              </div>
              {shownArtifacts.length === 0 ? (
                <p className="muted" style={{ fontSize: "var(--fs-sm)", padding: "0.5rem 0" }}>Nothing here matches the current filter.</p>
              ) : (
                <div>
                  {shownArtifacts.map((a) => (
                    <ArtifactRow key={a.id} a={a} subject={subjectById.get(a.subject_id)?.name ?? ""} onOpen={() => openArtifact(a)} />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Sessions + week line */}
          {shownSessions.length > 0 && (
            <>
              <div className="sec-head"><h2>Recent sessions</h2></div>
              <div>
                {shownSessions.map((x) => (
                  <div key={x.id} className="row" style={{ gap: "0.7rem", padding: "0.45rem 0.15rem", borderBottom: "1px solid var(--hairline)", flexWrap: "nowrap" }}>
                    <span className="tnum muted" style={{ fontSize: "var(--fs-xs)", width: 82, flexShrink: 0 }}>{x.studied_on}</span>
                    <span className="tnum" style={{ fontSize: "var(--fs-xs)", fontWeight: 600, color: "var(--accent-ink)", flexShrink: 0 }}>{formatMinutes(x.minutes)}</span>
                    <span className="muted" style={{ fontSize: "var(--fs-xs)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                      {[subjectById.get(x.subject_id)?.name, x.note].filter(Boolean).join(" — ")}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="row" style={{ justifyContent: "space-between", marginTop: "1.4rem", paddingTop: "0.9rem", borderTop: "1px solid var(--hairline)", flexWrap: "nowrap" }}>
            <div style={{ minWidth: 0 }}>
              <div className="seclabel">This week</div>
              <div className="serif tnum" style={{ fontSize: "var(--fs-md)", marginTop: 2 }}>
                {totalSessions > 0 ? `${formatMinutes(totalMinutes)} across ${totalSessions} session${totalSessions === 1 ? "" : "s"}` : "Nothing logged yet"}
              </div>
            </div>
            <button className="btn btn-pill btn-sm" style={{ flexShrink: 0 }} onClick={() => setLogSession(true)}>
              <img src={glyphClock} alt="" style={{ width: 14 }} /> Log a session
            </button>
          </div>

          <button className="asker" style={{ marginTop: "1rem" }} onClick={() => setChatOpen(true)}>
            <Sparkles size={15} /><span>Ask, log time, or find something…</span>
          </button>
        </>
      )}
      {sharedOverlays}
    </main>
  );
}

function AddDeskButton({ onAdded }: { onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return <button className="desk desk-new" onClick={() => setOpen(true)}><Plus size={15} /> New desk</button>;
  return (
    <form className="desk desk-new row" style={{ flexWrap: "nowrap", gap: "0.4rem" }}
      onSubmit={(e) => { e.preventDefault(); const n = name.trim(); if (!n || busy) return; setBusy(true); void store.createSubject(n).then(() => { setName(""); setOpen(false); setBusy(false); onAdded(); }); }}>
      <input placeholder="e.g. Biology" value={name} autoFocus onChange={(e) => setName(e.target.value)} style={{ minWidth: 0, flex: 1, minHeight: 38 }} />
      <button type="submit" className="btn btn-primary btn-sm" disabled={!name.trim() || busy}>Add</button>
      <button type="button" className="icon-btn" aria-label="Cancel" onClick={() => { setOpen(false); setName(""); }}><X size={14} /></button>
    </form>
  );
}

// Library row — a ruled list of things you read (design: .tile is now a list row).
function ArtifactRow({ a, subject, onOpen, extra }: { a: Artifact; subject: string; onOpen: () => void; extra?: string }) {
  return (
    <button className="tile" onClick={onOpen} style={{ width: "100%", cursor: "pointer", textAlign: "left" }}>
      <span className="thumb"><img src={KIND_GLYPH[a.kind]} alt="" /></span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="serif" style={{ display: "block", color: "var(--ink-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.title}</span>
        <span className="muted" style={{ display: "block", fontSize: "var(--fs-xs)", marginTop: 2 }}>
          {[KIND_LABELS[a.kind], subject, relativeDays(a.created_at.slice(0, 10))].filter(Boolean).join(" · ")}{extra ? ` · ${extra}` : ""}
        </span>
      </span>
    </button>
  );
}

// ── R1 — THE SUBJECT DESK: its materials and everything made from them, together ──
function DeskPage({ subject, artifacts, sessions, firstArtifactMoment, onBack, onAddMaterial, onDigestMaterial, onExplain, onPlan, onLog, onOpenArtifact, onDeleteSession, onAskLine }: {
  subject: Subject; artifacts: Artifact[]; sessions: StudySession[]; firstArtifactMoment: boolean;
  onBack: () => void; onAddMaterial: () => void; onDigestMaterial: (m: Material) => void;
  onExplain: () => void; onPlan: () => void; onLog: () => void;
  onOpenArtifact: (a: Artifact) => void; onDeleteSession: (id: string) => void; onAskLine: () => void;
}) {
  const [materials, setMaterials] = useState<Material[]>([]);
  useEffect(() => {
    let live = true;
    store.listMaterials(subject.id).then((m) => { if (live) setMaterials(m); }).catch(() => { /* desk still renders */ });
    return () => { live = false; };
  }, [subject.id, artifacts.length]);
  const digestedIds = new Set(artifacts.filter((a) => a.material_id).map((a) => a.material_id));
  const gap = gapCopy(subject);
  return (
    <main className="page">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: "1rem", flexWrap: "nowrap" }}>
        <button className="btn-ghost" onClick={onBack}><ArrowLeft size={15} /> Desks</button>
      </div>
      <div style={{ borderLeft: "3px solid var(--accent)", paddingLeft: 14, marginBottom: "1.1rem" }}>
        <h2 className="serif" style={{ fontSize: "1.85rem", margin: 0 }}>{subject.name}</h2>
        <div className="serif muted" style={{ fontSize: "var(--fs-md)", marginTop: 2 }}>
          {subject.last_studied ? `Studied ${gap.text}` : gap.text}{subject.total_minutes > 0 ? ` · ${formatMinutes(subject.total_minutes)} in all` : ""}
        </div>
      </div>
      <div className="row" style={{ gap: "0.5rem", marginBottom: "1.3rem", flexWrap: "nowrap" }}>
        <button className="btn btn-primary" style={{ flex: 1 }} onClick={onAddMaterial}><Plus size={15} /> Material</button>
        <button className="btn" onClick={onLog}><img src={glyphClock} alt="" style={{ width: 15 }} /> Log</button>
      </div>

      {firstArtifactMoment && (
        <div className="notice notice-info fade-up" style={{ marginBottom: "1rem", alignItems: "center" }}>
          <img src={momentFirstArtifact} alt="" style={{ width: 56, flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>It's on the {subject.name} desk.</div>
            <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Notes stay with the material they came from. Quiz yourself whenever — that part is free.</div>
          </div>
        </div>
      )}

      <div className="sec-head"><h2>Material on this desk</h2></div>
      {materials.length === 0 ? (
        <p className="muted" style={{ fontSize: "var(--fs-sm)", padding: "0.2rem 0 0.6rem" }}>Nothing on it yet — bring a passage or photograph a page.</p>
      ) : materials.map((m) => (
        <div key={m.id} className="material" style={{ marginBottom: "0.6rem" }}>
          <div className="material-body">
            <span className="thumb page" style={{ display: "grid", placeItems: "center" }}><img src={glyphMaterial} alt="" style={{ width: "55%" }} /></span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="material-title">{m.title || "Untitled material"}</div>
              <div className="material-meta">{[m.source === "photo" ? "Photo" : m.source === "lecture" ? "Lecture" : "Pasted", m.topic, relativeDays(m.created_at.slice(0, 10))].filter(Boolean).join(" · ")}</div>
              {digestedIds.has(m.id)
                ? <span className="badge badge-make">Digested</span>
                : <span className="badge badge-need">Nothing made from it yet</span>}
            </div>
          </div>
          <div className="material-actions">
            <button className={digestedIds.has(m.id) ? undefined : "suggested"} onClick={() => onDigestMaterial(m)}>{digestedIds.has(m.id) ? "Digest again" : "Digest"}</button>
            <button onClick={onExplain}>Explain</button>
            <button onClick={onPlan}>Plan</button>
          </div>
        </div>
      ))}

      {artifacts.length > 0 && (
        <>
          <div className="sec-head"><h2>Made from it</h2></div>
          <div>
            {artifacts.map((a) => {
              const b = parseArtifactBody(a);
              const extra = b?.kind === "notes"
                ? `${b.notes.key_concepts.length} concepts · ${b.notes.self_check.length} self-checks`
                : b?.kind === "plan" ? `${b.plan.weeks.length} weeks` : undefined;
              return <ArtifactRow key={a.id} a={a} subject="" extra={extra} onOpen={() => onOpenArtifact(a)} />;
            })}
          </div>
        </>
      )}

      {sessions.length > 0 && (
        <>
          <div className="sec-head"><h2>Sessions</h2></div>
          <div>
            {sessions.map((x) => (
              <div key={x.id} className="row" style={{ gap: "0.7rem", padding: "0.4rem 0.15rem", borderBottom: "1px solid var(--hairline)", flexWrap: "nowrap" }}>
                <span className="tnum muted" style={{ fontSize: "var(--fs-xs)", width: 82, flexShrink: 0 }}>{x.studied_on}</span>
                <span className="tnum" style={{ fontSize: "var(--fs-xs)", fontWeight: 600, color: "var(--accent-ink)", flexShrink: 0 }}>{formatMinutes(x.minutes)}</span>
                <span className="muted" style={{ fontSize: "var(--fs-xs)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{x.note}</span>
                <button className="icon-btn danger" title="Remove session" aria-label="Remove session" onClick={() => onDeleteSession(x.id)} style={{ flexShrink: 0 }}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        </>
      )}

      <button className="asker" style={{ marginTop: "1.2rem" }} onClick={onAskLine}>
        <Sparkles size={15} /><span>Ask, log time, or find something…</span>
      </button>
    </main>
  );
}

function AddSubjectInline({ onAdded, compact, autoFocus, onCreated }: { onAdded: () => void; compact?: boolean; autoFocus?: boolean; onCreated?: (s: Subject) => void }) {
  const [open, setOpen] = useState(!compact);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  async function add() {
    const n = name.trim();
    if (!n || busy) return;
    setBusy(true);
    try { const created = await store.createSubject(n); setName(""); if (compact) setOpen(false); onCreated?.(created); onAdded(); }
    finally { setBusy(false); }
  }
  if (compact && !open) {
    return <button className="chip toggle" onClick={() => setOpen(true)} aria-label="Add subject"><Plus size={12} /> subject</button>;
  }
  return (
    <form className="row" style={{ gap: "0.4rem", flexWrap: "nowrap", marginTop: compact ? 0 : "1.1rem", justifyContent: "center" }}
      onSubmit={(e) => { e.preventDefault(); void add(); }}>
      <input placeholder="e.g. Biology" value={name} autoFocus={autoFocus || compact} onChange={(e) => setName(e.target.value)}
        style={{ maxWidth: compact ? 150 : 220, fontSize: "var(--fs-sm)" }} />
      <button type="submit" className="btn btn-primary btn-sm btn-pill" style={{ flexShrink: 0 }} disabled={!name.trim() || busy}>Add</button>
      {compact && <button type="button" className="icon-btn" aria-label="Cancel" onClick={() => { setOpen(false); setName(""); }}><X size={14} /></button>}
    </form>
  );
}

function SubjectManagerModal({ subjects, onClose, onChanged }: { subjects: Subject[]; onClose: () => void; onChanged: () => void }) {
  const [renaming, setRenaming] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [confirm, setConfirm] = useState<Subject | null>(null);
  async function saveRename(id: string) {
    const n = name.trim();
    if (n) { await store.renameSubject(id, n); onChanged(); }
    setRenaming(null); setName("");
  }
  async function del(s: Subject) {
    setConfirm(null);
    await store.deleteSubject(s.id);
    onChanged();
  }
  return (
    <ModalShell title="Subjects" onCancel={onClose}>
      {confirm ? (
        <>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.25rem", lineHeight: 1.55 }}>
            Delete "<strong style={{ color: "var(--ink-1)" }}>{confirm.name}</strong>" and everything filed under it —
            {" "}{confirm.material_count} material{confirm.material_count === 1 ? "" : "s"}, {confirm.artifact_count} saved artifact{confirm.artifact_count === 1 ? "" : "s"}, {confirm.session_count} session{confirm.session_count === 1 ? "" : "s"}? This can't be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn btn-pill" onClick={() => setConfirm(null)}>Cancel</button>
            <button className="btn btn-danger btn-pill" onClick={() => void del(confirm)}>Delete subject</button>
          </div>
        </>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
          {subjects.map((s) => (
            <div key={s.id} className="row" style={{ justifyContent: "space-between", gap: "0.5rem", padding: "0.5rem 0.6rem", background: "var(--elev-2)", borderRadius: 12 }}>
              {renaming === s.id ? (
                <form className="row" style={{ gap: "0.4rem", flex: 1, flexWrap: "nowrap" }} onSubmit={(e) => { e.preventDefault(); void saveRename(s.id); }}>
                  <input value={name} autoFocus onChange={(e) => setName(e.target.value)} style={{ flex: 1, minWidth: 0, fontSize: "var(--fs-sm)" }} />
                  <button type="submit" className="btn btn-sm btn-pill" disabled={!name.trim()}>Save</button>
                </form>
              ) : (
                <>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{s.name}</div>
                    <div className="muted tnum" style={{ fontSize: 11 }}>
                      {s.material_count} material{s.material_count === 1 ? "" : "s"} · {s.artifact_count} saved · {s.last_studied ? `studied ${relativeDays(s.last_studied)}` : "not studied yet"}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 2, flexShrink: 0 }}>
                    <button className="icon-btn" title="Rename" aria-label={`Rename ${s.name}`} onClick={() => { setRenaming(s.id); setName(s.name); }}><Pencil size={14} /></button>
                    <button className="icon-btn danger" title="Delete" aria-label={`Delete ${s.name}`} onClick={() => setConfirm(s)}><Trash2 size={14} /></button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {!confirm && (
        <div className="row" style={{ justifyContent: "flex-end", marginTop: "1.25rem" }}>
          <button className="btn btn-pill" onClick={onClose}>Close</button>
        </div>
      )}
    </ModalShell>
  );
}

// ── Shared error rendering for the three billed actions ─────────────────────────
type AskErr = { code: BrainErrorCode | "empty"; message: string };

function BrainErrorNotice({ err, onRetry }: { err: AskErr; onRetry?: () => void }) {
  if (err.code === "refused_academic") {
    return (
      <div className="notice notice-warn fade-up" style={{ marginTop: 12 }}>
        <AlertCircle size={15} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ color: "var(--ink-2)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "insufficient_funds") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Coins size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>Add funds to keep going</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then try again.</div>
        </div>
      </div>
    );
  }
  if (err.code === "managed_ai_blocked" || err.code === "unauthenticated") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Lock size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "empty") {
    return <div className="muted fade-up" style={{ fontSize: "var(--fs-sm)", marginTop: 12 }}>{err.message}</div>;
  }
  return (
    <div className="notice notice-err fade-up" style={{ marginTop: 12, justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: "var(--fs-sm)" }}>{err.message}</span>
      {onRetry && (
        <button className="btn btn-sm btn-pill" style={{ flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

function LoadingLines({ label }: { label: string }) {
  return (
    <div aria-live="polite" style={{ marginTop: "0.85rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
      <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
        <Sparkles size={12} style={{ color: "var(--accent)" }} className="pulse-soft" /> {label}
      </span>
      <div className="skeleton-line" style={{ width: "100%" }} />
      <div className="skeleton-line" style={{ width: "92%" }} />
      <div className="skeleton-line" style={{ width: "70%" }} />
    </div>
  );
}

// Ghost copy-button for AI results (DESIGN-SYSTEM §7.5 — every Oriora result is copyable).
function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { /* clipboard blocked — silently no-op */ }
  }, [text]);
  return (
    <button className="copy-btn" onClick={copy} aria-label={done ? "Copied" : label} title={done ? "Copied" : label}>
      <span aria-hidden>{done ? <Check size={13} /> : <Copy size={13} />}</span>
      <span className="copy-btn-lbl">{done ? "Copied" : label}</span>
      <span aria-live="polite" className="sr-only">{done ? "Copied to clipboard" : ""}</span>
    </button>
  );
}

function SubjectSelect({ subjects, value, onChange }: { subjects: Subject[]; value: string; onChange: (id: string) => void }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={{ marginBottom: "1rem" }}>
      {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
    </select>
  );
}

// ── Plain-text renderings (copy + read-aloud share these) ───────────────────────
function notesToText(n: StudyNotes): string {
  const parts: string[] = [];
  if (n.summary) parts.push(n.summary);
  if (n.key_concepts.length) parts.push("Key concepts:\n" + n.key_concepts.map((c) => `• ${c.term} — ${c.definition}`).join("\n"));
  if (n.self_check.length) parts.push("Check yourself:\n" + n.self_check.map((q, i) => `${i + 1}. ${q.question}\n   ${q.explanation}`).join("\n"));
  return parts.join("\n\n");
}
function explanationToText(x: Explanation): string {
  return [
    x.concept ? `${x.concept}` : "",
    x.analogy ? `Think of it like this: ${x.analogy}` : "",
    x.definition ? `Precisely: ${x.definition}` : "",
    x.worked_example ? `Worked example: ${x.worked_example}` : "",
    x.misconception ? `Watch out: ${x.misconception}` : "",
  ].filter(Boolean).join("\n\n");
}
function planToText(p: StudyPlan): string {
  const weeks = p.weeks.map((w) =>
    `Week ${w.week}${w.label ? ` — ${w.label}` : ""}\n` +
    (w.focus.length ? w.focus.map((f) => `  • ${f}`).join("\n") + "\n" : "") +
    (w.milestone ? `  Milestone: ${w.milestone}\n` : "") +
    (w.review ? `  Review: ${w.review}` : "")).join("\n");
  return [p.goal, weeks, p.note].filter(Boolean).join("\n\n");
}
function bodyToText(b: ArtifactBody | null): string {
  if (!b) return "";
  if (b.kind === "notes") return notesToText(b.notes);
  if (b.kind === "explanation") return explanationToText(b.explanation);
  return planToText(b.plan);
}

// ── R2 — INTAKE, the front door. A full page: bring material, then choose its verb. ──
function IntakePage({ subjects, defaultSubjectId, offline, initialText, initialTitle, initialTopic, materialId, onExplain, onPlan, onClose, onSaved }: {
  subjects: Subject[]; defaultSubjectId: string; offline: boolean;
  initialText?: string; initialTitle?: string; initialTopic?: string;
  /** Set when digesting a material already on the desk — the notes attach to it, no new material. */
  materialId?: string;
  onExplain: () => void; onPlan: () => void;
  onClose: () => void; onSaved: (subjectId: string, firstArtifactEver: boolean) => void;
}) {
  const [subjectId, setSubjectId] = useState(defaultSubjectId);
  const [title, setTitle] = useState(initialTitle ?? "");
  const [topic, setTopic] = useState(initialTopic ?? "");
  const [text, setText] = useState(initialText ?? "");
  const [source, setSource] = useState<"typed" | "photo" | "lecture">("typed");
  const [keeping, setKeeping] = useState(false);
  const [scanMsg, setScanMsg] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<AskErr | null>(null);
  const [result, setResult] = useState<StudyNotes | null>(null);
  const [saving, setSaving] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);

  // Import a PDF (lecture slides, a handout) → on-device pdfjs text extraction → the material box.
  // The file's bytes never leave the device; only text the user then digests transits.
  async function importPdf(file: File) {
    setScanning(true); setScanMsg("Reading the PDF on this device…");
    try {
      const { extractPdfText } = await import("./pdf");
      const r = await extractPdfText(await file.arrayBuffer());
      if (!r.text.trim()) { setScanMsg("No selectable text in that PDF — if it's a scan, photograph the pages instead."); return; }
      setText((cur) => (cur ? cur + "\n\n" + r.text : r.text));
      setScanMsg(`Read ${r.pages} page${r.pages === 1 ? "" : "s"}${r.truncated ? " (long file — trimmed)" : ""} — check the text below before digesting.`);
    } catch { setScanMsg("Couldn't read that PDF — paste the text instead."); }
    finally { setScanning(false); }
  }
  // Every page the user photographs for THIS material. Was dropped after OCR, so "I added an
  // image" showed nothing anywhere; a chapter is several pages, so this is a list.
  const [pagePaths, setPagePaths] = useState<string[]>([]);
  const [pagePreview, setPagePreview] = useState<Record<string, string>>({});
  const [viewingPage, setViewingPage] = useState<number | null>(null);
  // Live, instant client-side hint so the user sees the fence BEFORE pressing the button.
  const fenceHint = useMemo(() => academicRefusal(text.slice(0, 2000)), [text]);

  // Photograph a textbook page / handwritten notes → on-device OCR (Apple Vision) → the text
  // lands in the material box for review. The photo never leaves the device.
  async function runOcr(path: string) {
    setScanning(true); setScanMsg("Reading the photo…");
    try {
      let t = "";
      try { t = (await store.ocrImage(path)).trim(); }
      catch { setScanMsg("On-device scan isn't available here — paste or type the text instead."); return; }
      if (!t) { setScanMsg("Couldn't read any text — try a clearer, straight-on photo, or paste it."); return; }
      setText((cur) => (cur ? cur + "\n\n" + t : t));
      setSource("photo");
      setPagePaths((p) => [...p, path]);   // keep the page so it shows, and saves with the material
      setScanMsg(`Check the text below against the page — Vision gets most of it, not all of it. Add another page any time.`);
    } finally { setScanning(false); }
  }

  async function scanPhoto() {
    setScanMsg(null);
    // iOS: openFileDialog is a no-op → tap a hidden <input> to raise the native photo/camera
    // sheet. platform() is sync so .click() stays inside the tap's user-gesture (required).
    if (platform() === "ios") { photoInputRef.current?.click(); return; }
    try {
      const picked = await openFileDialog({ multiple: false, filters: [{ name: "Image", extensions: ["png", "jpg", "jpeg", "heic", "webp"] }] });
      if (picked && typeof picked === "string") { await runOcr(picked); }
    } catch { /* user cancelled */ }
  }

  async function onPhotoInputChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = ""; // reset so re-picking the same photo refires
    if (!file) return;
    setScanMsg("Reading…");
    try {
      const ext = (file.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
      const bytes = Array.from(new Uint8Array(await file.arrayBuffer()));
      const path = await store.savePagePhoto(bytes, ext);
      await runOcr(path);
    } catch { setScanMsg("Couldn't read that photo — try again."); }
  }

  async function run() {
    setBusy(true); setErr(null); setResult(null);
    try {
      const r = await digestMaterial(text, { title: title.trim(), topic: topic.trim() });
      if (r.refusal) { setErr({ code: "refused_academic", message: r.refusal }); return; }
      if (!r.summary && r.key_concepts.length === 0) {
        setErr({ code: "empty", message: "Nothing came back — try a longer or cleaner passage." });
        return;
      }
      setResult(r);
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "The request failed — please try again." });
    } finally { setBusy(false); }
  }

  async function save() {
    if (!result) return;
    setSaving(true);
    try {
      const label = title.trim() || result.key_concepts[0]?.term || "Study notes";
      // Digesting a material already on the desk reuses it (same as the chat's digest path);
      // creating one here duplicated the material on every desk "Digest" / "Digest again".
      const material = materialId ? { id: materialId } : await store.createMaterial({
        subject_id: subjectId, topic: topic.trim(), title: label, source, content: text.trim(),
      });
      // The photographed pages travel WITH the material, so the library shows what you added.
      for (const p of pagePaths) { try { await store.addMaterialPhoto(material.id, p); } catch { /* a page failing to attach must not lose the notes */ } }
      const before = await store.listArtifacts();
      await store.createArtifact({
        subject_id: subjectId, material_id: material.id, kind: "notes", title: label,
        body: JSON.stringify({ key_concepts: result.key_concepts, summary: result.summary, self_check: result.self_check } satisfies StudyNotes),
      });
      onSaved(subjectId, before.length === 0);
    } catch { setErr({ code: "unavailable", message: "Couldn't save — try again." }); setSaving(false); }
  }

  // "Just keep it on the desk" — save the material with no billed call.
  async function keepOnDesk() {
    if (keeping) return;
    setKeeping(true);
    try {
      const label = title.trim() || text.trim().slice(0, 42) || "Untitled material";
      const material = await store.createMaterial({ subject_id: subjectId, topic: topic.trim(), title: label, source, content: text.trim() });
      for (const pth of pagePaths) { try { await store.addMaterialPhoto(material.id, pth); } catch { /* keep the material anyway */ } }
      onSaved(subjectId, false);
    } catch { setErr({ code: "unavailable", message: "Couldn't save — try again." }); setKeeping(false); }
  }

  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  return (
    <main className="page">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.9rem", flexWrap: "nowrap" }}>
        <button className="btn-ghost" onClick={onClose}><ArrowLeft size={15} /> Cancel</button>
      </div>
      <h2 className="serif" style={{ fontSize: "1.6rem", margin: "0 0 1rem" }}>New material</h2>
      {!result && (
        <>
          <div className="row" style={{ gap: "0.6rem", flexWrap: "nowrap", marginBottom: "0.75rem" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={fieldLabel}>Desk</label>
              {/* READY-STATE R2 — the door must not dead-end. With zero subjects SubjectSelect
                  renders an EMPTY <select>, so subjectId stays "" and "Keep on desk" is disabled
                  forever: the first screen's own primary CTA led here and could not be completed,
                  with nothing on screen saying why. The only escape was Cancel, back to the home
                  screen, to use the subject field there. Fault shape 2 — the guard is in the
                  CALLER (the save is gated on subjectId; the control that produces one lived on
                  another screen). Measured 2026-09-25: Keep enabled=false at zero data.
                  Same component the home screen already uses; it now hands back the row it made
                  so this page can select it immediately. */}
              {subjects.length === 0
                ? <AddSubjectInline onAdded={() => { /* parent reloads on save */ }} onCreated={(sub) => setSubjectId(sub.id)} autoFocus={false} />
                : <SubjectSelect subjects={subjects} value={subjectId} onChange={setSubjectId} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={fieldLabel}>Topic <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></label>
              <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. osmosis" />
            </div>
          </div>

          <label style={fieldLabel}>Title <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Ch. 4 — Cell transport" style={{ marginBottom: "0.75rem" }} />

          <label style={fieldLabel}>The material</label>
          <textarea rows={7} placeholder="Paste the passage, your lecture notes, or scan a page…" value={text}
            onChange={(e) => setText(e.target.value)} style={{ marginBottom: "0.5rem", resize: "vertical" }} />
          <div className="row" style={{ gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.4rem" }}>
            <button type="button" className="btn btn-sm btn-pill row" style={{ gap: "0.35rem" }} onClick={scanPhoto} disabled={scanning}>
              <Camera size={13} /> {scanning ? "Reading…" : "Photograph a page"}
            </button>
            <LectureRecorder disabled={scanning}
              onTranscript={(t) => {
                setText((cur) => (cur ? cur.replace(/\s*$/, " ") + t : t));
                setSource((s) => (s === "typed" ? "lecture" : s));
              }} />
            <button type="button" className="btn btn-sm btn-pill row" style={{ gap: "0.35rem" }} disabled={scanning} onClick={() => pdfInputRef.current?.click()}>
              <FileText size={13} /> Import a PDF
            </button>
            <span className="intake-privacy">Read and transcribed on your device — no photo, audio or file is ever uploaded.</span>
          </div>
          <input ref={pdfInputRef} type="file" accept="application/pdf" style={{ display: "none" }}
            onChange={(ev) => { const f = ev.target.files?.[0]; ev.target.value = ""; if (f) void importPdf(f); }} />
          {/* Hidden input raises the native iOS photo/camera sheet (desktop uses the file dialog). */}
          <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoInputChange} style={{ display: "none" }} />
          {scanMsg && <div className="muted" style={{ fontSize: "0.8rem", marginBottom: "0.4rem" }}>{scanMsg}</div>}
          {viewingPage !== null && (
            <PhotoViewer
              photos={pagePaths.map((p, i) => ({ src: pagePreview[p] ?? "", alt: `Page ${i + 1}` })).filter((v) => v.src) as ViewerPhoto[]}
              startIndex={Math.min(viewingPage, pagePaths.length - 1)}
              onClose={() => setViewingPage(null)}
            />
          )}

          {/* The pages you added, VISIBLE. Adding an image used to show nothing anywhere. */}
          {pagePaths.length > 0 && (
            <div style={{ marginBottom: "0.6rem" }}>
              <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginBottom: 4 }}>
                {pagePaths.length === 1 ? "One page read" : `${pagePaths.length} pages read`} — tap “Photograph a page” again for the next one.
              </div>
              <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 2 }}>
                {pagePaths.map((p, i) => (
                  <div key={p} style={{ position: "relative", flexShrink: 0 }}>
                    <PagePhoto path={p} alt={`Page ${i + 1}`}
                      onLoaded={(u) => setPagePreview((m) => (m[p] === u ? m : { ...m, [p]: u }))}
                      onOpen={() => setViewingPage(i)} />
                    <button type="button" aria-label={`Remove page ${i + 1}`} title="Remove this page"
                      onClick={() => setPagePaths((cur) => cur.filter((x) => x !== p))}
                      style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: 999, lineHeight: 1,
                               border: "1px solid var(--border)", background: "var(--elev-2)", cursor: "pointer", padding: 0, fontSize: 12 }}>×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Study-tool fence — instant, client-side, BEFORE any billed call. */}
          {fenceHint && (
            <div className="notice notice-warn" style={{ marginTop: 6 }}>
              <AlertCircle size={14} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
              <span>{fenceHint}</span>
            </div>
          )}

          <div className="muted tnum" style={{ fontSize: "var(--fs-xs)", margin: "0.4rem 0 1rem" }}>
            {words > 0 ? `${words} word${words === 1 ? "" : "s"}${text.trim().length >= 40 ? " · enough to digest" : ""}` : ""}
          </div>

          <div className="sec-head" style={{ marginTop: "0.4rem" }}><h2>Then what?</h2></div>
          <div className="verbs" style={{ marginBottom: "0.7rem" }}>
            <button className="verb primary" disabled={busy || !!fenceHint || offline || text.trim().length < 40 || !subjectId} onClick={run}>
              <img src={glyphNotes} alt="" style={{ width: 20, filter: "brightness(0) invert(1)", opacity: 0.9 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="verb-name">{busy ? "Digesting…" : "Digest it into notes"}</span>
                <span className="verb-sub">Key concepts, a summary, self-checks</span>
              </span>
              <span className="verb-cost">1 call</span>
            </button>
            <button className="verb" onClick={onExplain}>
              <img src={glyphExplanation} alt="" style={{ width: 20 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="verb-name">Explain one concept from it</span>
                <span className="verb-sub">Analogy, definition, example, the trap</span>
              </span>
              <span className="verb-cost">1 call</span>
            </button>
            <button className="verb" onClick={onPlan}>
              <img src={glyphPlan} alt="" style={{ width: 20 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="verb-name">Plan the run-up to a deadline</span>
                <span className="verb-sub">Weeks, milestones, review points</span>
              </span>
              <span className="verb-cost">1 call</span>
            </button>
            <button className="verb" style={{ borderStyle: "dashed", minHeight: 46 }} disabled={keeping || !subjectId || text.trim().length === 0} onClick={() => void keepOnDesk()}>
              <span className="verb-name" style={{ fontWeight: 500, color: "var(--ink-2)" }}>{keeping ? "Keeping…" : "Just keep it on the desk"}</span>
            </button>
          </div>
          <p className="muted" style={{ fontSize: "var(--fs-xxs)", lineHeight: 1.5 }}>
            Your material stays on your device unless you back it up; the text you digest passes through Oriora, which doesn't retain it.
          </p>

          {busy && <LoadingLines label="Turning your material into study notes…" />}
          {err && !busy && <BrainErrorNotice err={err} onRetry={run} />}
        </>
      )}

      {result && (
        <div className="fade-up" aria-live="polite">
          <NotesView n={result} />
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          <div className="row" style={{ marginTop: 12, justifyContent: "space-between", gap: "0.6rem" }}>
            <span className="row" style={{ gap: "0.35rem" }}>
              <CopyButton text={notesToText(result)} />
              <SpeakButton text={notesToText(result)} />
            </span>
            <span className="row" style={{ gap: "0.5rem" }}>
              <button className="btn btn-pill" onClick={() => setResult(null)} disabled={saving}>Back</button>
              <button className="btn btn-primary btn-pill" onClick={save} disabled={saving}>
                {saving ? (<><Sparkles size={13} className="pulse-soft" /> Saving…</>) : "Save to library"}
              </button>
            </span>
          </div>
          {err && <BrainErrorNotice err={err} />}
        </div>
      )}
    </main>
  );
}

function NotesView({ n, highlights, onToggleHighlight, onGoDeeper }: {
  n: StudyNotes;
  highlights?: string[];
  onToggleHighlight?: (term: string) => void;
  onGoDeeper?: (term: string) => void;
}) {
  const [revealed, setRevealed] = useState<Set<number>>(new Set());
  const marked = new Set(highlights ?? []);
  return (
    <div>
      {n.summary && <p className="reader-lede" style={{ whiteSpace: "pre-wrap" }}>{n.summary}</p>}
      {n.key_concepts.length > 0 && (
        <>
          <div className="sec-head"><h2>Key concepts</h2>
            {onToggleHighlight && <span className="muted" style={{ fontSize: "var(--fs-xxs)" }}>tap to mark</span>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem", marginBottom: "1rem" }}>
            {n.key_concepts.map((c, i) => {
              const on = marked.has(c.term);
              return (
              <div key={i} role={onToggleHighlight ? "button" : undefined}
                className={`concept${on ? " marked" : ""}`}
                onClick={onToggleHighlight ? () => onToggleHighlight(c.term) : undefined}
                style={{ cursor: onToggleHighlight ? "pointer" : undefined }}>
                <div className="row" style={{ justifyContent: "space-between", gap: 6, flexWrap: "nowrap" }}>
                  <div className="concept-term">{c.term}</div>
                  {onGoDeeper && (
                    <button type="button" className="concept-deeper" style={{ background: "none", border: 0, padding: 0, cursor: "pointer" }}
                      onClick={(e) => { e.stopPropagation(); onGoDeeper(c.term); }}>Explain →</button>
                  )}
                </div>
                <div className="concept-def">{c.definition}</div>
              </div>
            );})}
          </div>
        </>
      )}
      {n.self_check.length > 0 && (
        <>
          <div className="sec-head"><h2><img src={glyphSelfCheck} alt="" style={{ width: 15, verticalAlign: "-2px", marginRight: 5 }} />Check yourself</h2></div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
            {n.self_check.map((q, i) => (
              <div key={i} className="selfcheck">
                <div className="selfcheck-q">{q.question}</div>
                {revealed.has(i) ? (
                  <div className="selfcheck-a">{q.explanation}</div>
                ) : (
                  <button className="link-quiet" style={{ color: "var(--accent-ink)", textDecoration: "none" }}
                    onClick={() => setRevealed((s) => new Set(s).add(i))}>Show the thinking →</button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── EXPLAIN — concept + level → layered explanation ─────────────────────────────
function ExplainModal({ subjects, defaultSubjectId, offline, initialConcept, onClose, onSaved }: {
  subjects: Subject[]; defaultSubjectId: string; offline: boolean; initialConcept?: string; onClose: () => void; onSaved: () => void;
}) {
  const [subjectId, setSubjectId] = useState(defaultSubjectId);
  const [concept, setConcept] = useState(initialConcept ?? "");
  const [level, setLevel] = useState<Level>("new");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<AskErr | null>(null);
  const [result, setResult] = useState<Explanation | null>(null);
  const [saving, setSaving] = useState(false);
  const [photoNote, setPhotoNote] = useState<string | null>(null);
  const fenceHint = useMemo(() => academicRefusal(concept), [concept]);
  const subjectName = subjects.find((s) => s.id === subjectId)?.name ?? "";

  async function run() {
    setBusy(true); setErr(null); setResult(null);
    try {
      const r = await explainConcept(concept, level, subjectName);
      if (r.refusal) { setErr({ code: "refused_academic", message: r.refusal }); return; }
      if (!r.definition && !r.analogy) { setErr({ code: "empty", message: "Nothing came back — try naming the concept differently." }); return; }
      setResult(r);
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "The request failed — please try again." });
    } finally { setBusy(false); }
  }

  async function save() {
    if (!result) return;
    setSaving(true);
    try {
      await store.createArtifact({
        subject_id: subjectId, kind: "explanation", title: result.concept || concept.trim(),
        body: JSON.stringify({ concept: result.concept, analogy: result.analogy, definition: result.definition, worked_example: result.worked_example, misconception: result.misconception } satisfies Explanation),
      });
      onSaved();
    } catch { setErr({ code: "unavailable", message: "Couldn't save — try again." }); setSaving(false); }
  }

  return (
    <ModalShell title="Explain a concept" subtitle="Simple analogy → precise definition → worked example → the classic mistake" onCancel={onClose} wide>
      {!result && (
        <>
          <label style={fieldLabel}>Subject</label>
          <SubjectSelect subjects={subjects} value={subjectId} onChange={setSubjectId} />

          <label style={fieldLabel}>The concept</label>
          <div className="row" style={{ gap: "0.35rem", flexWrap: "nowrap", marginBottom: "0.35rem" }}>
            <input value={concept} autoFocus onChange={(e) => setConcept(e.target.value)}
              placeholder="e.g. osmosis, the subjunctive, eventual consistency"
              style={{ flex: 1, minWidth: 0 }}
              onKeyDown={(e) => { if (e.key === "Enter" && !busy && !fenceHint && !offline && concept.trim().length >= 2) void run(); }} />
            {/* Bogdan: "explain concept, I didn't have option to upload photo or nothing."
                Photograph the thing you don't understand — read on-device, so it costs nothing. */}
            <PhotoButton
              onNotice={setPhotoNote}
              onText={(t) => { setPhotoNote(null); setConcept((v) => (v ? v + " " : "") + t); }}
              disabled={busy} />
            <MicButton onText={(t) => setConcept((v) => (v ? v + " " : "") + t)} disabled={busy} />
          </div>
          {photoNote && <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginBottom: "0.5rem" }}>{photoNote}</div>}
          <div style={{ marginBottom: "1rem" }} />

          <label style={fieldLabel}>Where you're at</label>
          <div className="row" style={{ gap: "0.4rem", marginBottom: "0.6rem", flexWrap: "wrap" }}>
            {LEVELS.map((l) => (
              <button key={l.id} type="button" className={`chip toggle${level === l.id ? " selected" : ""}`} onClick={() => setLevel(l.id)}>{l.label}</button>
            ))}
          </div>

          {fenceHint && (
            <div className="notice notice-warn" style={{ marginTop: 6 }}>
              <AlertCircle size={14} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
              <span>{fenceHint}</span>
            </div>
          )}

          <div className="row" style={{ justifyContent: "space-between", marginTop: "0.8rem", gap: "0.6rem" }}>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)", flex: 1, minWidth: 0 }}>One explanation = one Oriora call.</span>
            <button className="btn btn-primary btn-sm btn-pill" style={{ flexShrink: 0 }}
              disabled={busy || !!fenceHint || offline || concept.trim().length < 2 || !subjectId} onClick={run}>
              {busy ? (<><Sparkles size={12} className="pulse-soft" /> Explaining…</>) : (<><Lightbulb size={13} /> Explain</>)}
            </button>
          </div>

          {busy && <LoadingLines label="Building the layers…" />}
          {err && !busy && <BrainErrorNotice err={err} onRetry={run} />}
        </>
      )}

      {result && (
        <div className="fade-up" aria-live="polite">
          <ExplanationView x={result} />
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          <div className="row" style={{ marginTop: 12, justifyContent: "space-between", gap: "0.6rem" }}>
            <span className="row" style={{ gap: "0.35rem" }}>
              <CopyButton text={explanationToText(result)} />
              <SpeakButton text={explanationToText(result)} />
            </span>
            <span className="row" style={{ gap: "0.5rem" }}>
              <button className="btn btn-pill" onClick={() => setResult(null)} disabled={saving}>Back</button>
              <button className="btn btn-primary btn-pill" onClick={save} disabled={saving}>
                {saving ? (<><Sparkles size={13} className="pulse-soft" /> Saving…</>) : "Save to library"}
              </button>
            </span>
          </div>
          {err && <BrainErrorNotice err={err} />}
        </div>
      )}
    </ModalShell>
  );
}

function ExplanationView({ x, standalone }: { x: Explanation; standalone?: boolean }) {
  const layer = (label: string, body: string, cls = "", example = false) => body ? (
    <div className={`layer${cls}`}>
      <div className="layer-label">{label}</div>
      {example
        ? <div className="layer-example"><p style={{ whiteSpace: "pre-wrap" }}>{body}</p></div>
        : <p style={{ whiteSpace: "pre-wrap" }}>{body}</p>}
    </div>
  ) : null;
  return (
    <div className="reader">
      {x.concept && !standalone && <h1 style={{ fontSize: "var(--fs-xl)", margin: "0 0 1.1rem" }}>{x.concept}</h1>}
      <div className="layer-rail">
        {layer("Think of it like this", x.analogy)}
        {layer("Precisely", x.definition)}
        {layer("Worked example", x.worked_example, "", true)}
        {layer("Where people slip", x.misconception, " caution")}
      </div>
    </div>
  );
}

// ── PLAN — goal + deadline + topics + weekly hours → week-by-week plan ──────────
function PlanModal({ subjects, defaultSubjectId, offline, onClose, onSaved }: {
  subjects: Subject[]; defaultSubjectId: string; offline: boolean; onClose: () => void; onSaved: () => void;
}) {
  const [subjectId, setSubjectId] = useState(defaultSubjectId);
  const [goal, setGoal] = useState("");
  const [deadline, setDeadline] = useState("");
  const [hours, setHours] = useState("5");
  const [topics, setTopics] = useState<string[]>([]);
  const [topicInput, setTopicInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<AskErr | null>(null);
  const [result, setResult] = useState<StudyPlan | null>(null);
  const [saving, setSaving] = useState(false);
  const fenceHint = useMemo(() => academicRefusal(goal), [goal]);

  function addTopics() {
    setTopics(dedupeStrings([...topics, ...topicInput.split(/[,\n]/)]));
    setTopicInput("");
  }

  const weeklyHours = Math.max(1, Math.min(80, Number(hours) || 0));
  const ready = goal.trim().length > 0 && deadline && topics.length > 0 && Number(hours) > 0;

  async function run() {
    setBusy(true); setErr(null); setResult(null);
    try {
      const r = await buildPlan({ goal, deadline, weeklyHours, topics, today: todayLocal() });
      if (r.refusal) { setErr({ code: "refused_academic", message: r.refusal }); return; }
      if (r.weeks.length === 0) { setErr({ code: "empty", message: "No plan came back — check the deadline is in the future." }); return; }
      setResult(r);
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "The request failed — please try again." });
    } finally { setBusy(false); }
  }

  async function save() {
    if (!result) return;
    setSaving(true);
    try {
      await store.createArtifact({
        subject_id: subjectId, kind: "plan", title: result.goal || goal.trim(),
        body: JSON.stringify({ goal: result.goal, weeks: result.weeks, note: result.note } satisfies StudyPlan),
      });
      onSaved();
    } catch { setErr({ code: "unavailable", message: "Couldn't save — try again." }); setSaving(false); }
  }

  return (
    <ModalShell title="Plan the run-up" subtitle="Goal + deadline + topics → a week-by-week plan with milestones and review points" onCancel={onClose} wide>
      {!result && (
        <>
          <label style={fieldLabel}>Subject</label>
          <SubjectSelect subjects={subjects} value={subjectId} onChange={setSubjectId} />

          <label style={fieldLabel}>The goal</label>
          <input value={goal} autoFocus onChange={(e) => setGoal(e.target.value)}
            placeholder="e.g. pass the A2 Spanish exam" style={{ marginBottom: "0.75rem" }} />

          <div className="row" style={{ gap: "0.6rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={fieldLabel}>Deadline</label>
              <input type="date" value={deadline} min={todayLocal()} onChange={(e) => setDeadline(e.target.value)} />
            </div>
            <div style={{ width: 130 }}>
              <label style={fieldLabel}>Hours / week</label>
              <input type="number" inputMode="numeric" min={1} max={80} value={hours} onChange={(e) => setHours(e.target.value)} />
            </div>
          </div>

          <label style={fieldLabel}>Topics to cover</label>
          {topics.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.5rem" }}>
              {topics.map((t) => (
                <button key={t} type="button" className="chip selected" onClick={() => setTopics((cur) => cur.filter((x) => x !== t))}>{t} <X size={11} /></button>
              ))}
            </div>
          )}
          <form className="row" style={{ gap: "0.4rem", marginBottom: "0.6rem", flexWrap: "nowrap" }} onSubmit={(e) => { e.preventDefault(); addTopics(); }}>
            <input placeholder="Add topic(s), comma-separated…" value={topicInput} onChange={(e) => setTopicInput(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
            <button type="submit" className="btn btn-pill" style={{ flexShrink: 0 }} disabled={!topicInput.trim()}>Add</button>
          </form>

          {fenceHint && (
            <div className="notice notice-warn" style={{ marginTop: 6 }}>
              <AlertCircle size={14} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
              <span>{fenceHint}</span>
            </div>
          )}

          <div className="row" style={{ justifyContent: "space-between", marginTop: "0.8rem", gap: "0.6rem" }}>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)", flex: 1, minWidth: 0 }}>One plan = one Oriora call. It schedules learning — it never does the work.</span>
            <button className="btn btn-primary btn-sm btn-pill" style={{ flexShrink: 0 }}
              disabled={busy || !!fenceHint || offline || !ready || !subjectId} onClick={run}>
              {busy ? (<><Sparkles size={12} className="pulse-soft" /> Planning…</>) : (<><Calendar size={13} /> Build the plan</>)}
            </button>
          </div>

          {busy && <LoadingLines label="Laying out the weeks…" />}
          {err && !busy && <BrainErrorNotice err={err} onRetry={run} />}
        </>
      )}

      {result && (
        <div className="fade-up" aria-live="polite">
          <PlanView p={result} />
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          <div className="row" style={{ marginTop: 12, justifyContent: "space-between", gap: "0.6rem" }}>
            <span className="row" style={{ gap: "0.35rem" }}>
              <CopyButton text={planToText(result)} />
              <SpeakButton text={planToText(result)} />
            </span>
            <span className="row" style={{ gap: "0.5rem" }}>
              <button className="btn btn-pill" onClick={() => setResult(null)} disabled={saving}>Back</button>
              <button className="btn btn-primary btn-pill" onClick={save} disabled={saving}>
                {saving ? (<><Sparkles size={13} className="pulse-soft" /> Saving…</>) : "Save to library"}
              </button>
            </span>
          </div>
          {err && <BrainErrorNotice err={err} />}
        </div>
      )}
    </ModalShell>
  );
}

function PlanView({ p, standalone }: { p: StudyPlan; standalone?: boolean }) {
  return (
    <div className="reader">
      {p.goal && !standalone && <h1 style={{ fontSize: "var(--fs-xl)", margin: "0 0 1.1rem" }}>{p.goal}</h1>}
      <div className="plan-spine">
        {p.weeks.map((w) => (
          <div key={w.week} className="plan-week" data-week={w.week}>
            <h3>{w.label || `Week ${w.week}`}</h3>
            {w.focus.length > 0 && (
              <div className="plan-focus">
                {w.focus.map((f, i) => <div key={i}>— {f}</div>)}
              </div>
            )}
            {w.milestone && (
              <div className="plan-milestone" style={{ marginTop: 8 }}>
                <div className="layer-label">Milestone</div>
                <p>{w.milestone}</p>
              </div>
            )}
            {w.review && (
              <div className="plan-echo"><RefreshCw size={13} style={{ flexShrink: 0, marginTop: 2 }} /><span>{w.review}</span></div>
            )}
          </div>
        ))}
      </div>
      {p.note && <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.55, marginTop: "0.6rem" }}>{p.note}</p>}
    </div>
  );
}

// ── R3 — READING PAGE: an artifact is a page you read, not a modal ──────────────
function ReadPage({ a, subject, onBack, onDelete, onGoDeeper, onLog }: {
  a: Artifact; subject: string; onBack: () => void; onDelete: () => void;
  onGoDeeper?: (term: string) => void; onLog: () => void;
}) {
  const body = useMemo(() => parseArtifactBody(a), [a]);
  const text = useMemo(() => bodyToText(body), [body]);
  const [quizzing, setQuizzing] = useState(false);
  const [pages, setPages] = useState<MaterialPhoto[]>([]);
  const [pageUrls, setPageUrls] = useState<Record<string, string>>({});
  const [viewing, setViewing] = useState<number | null>(null);
  const [highlights, setHighlights] = useState<string[]>(
    body?.kind === "notes" ? (body.notes.highlights ?? []) : [],
  );

  useEffect(() => {
    let live = true;
    if (!a.material_id) return;
    store.listMaterialPhotos(a.material_id).then((p) => { if (live) setPages(p); }).catch(() => { /* no pages */ });
    return () => { live = false; };
  }, [a.material_id]);

  async function toggleHighlight(term: string) {
    if (body?.kind !== "notes") return;
    const next = highlights.includes(term) ? highlights.filter((t) => t !== term) : [...highlights, term];
    setHighlights(next);
    try { await store.updateArtifactBody(a.id, JSON.stringify({ ...body.notes, highlights: next })); }
    catch { setHighlights(highlights); }  // never lie about saved state
  }

  const notes = body?.kind === "notes" ? body.notes : null;
  const kicker = a.kind === "notes" ? "Study notes" : a.kind === "explanation" ? "Explanation" : "Plan";
  return (
    <main className="page">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: "1rem", flexWrap: "nowrap" }}>
        <button className="btn-ghost" onClick={onBack}><ArrowLeft size={15} /> {subject || "Back"}</button>
        <div className="row" style={{ gap: 2, flexWrap: "nowrap" }}>
          {text && <CopyButton text={text} label="" />}
          {text && <SpeakButton text={text} />}
          <button className="icon-btn danger" title="Delete" aria-label="Delete" onClick={onDelete}><Trash2 size={15} /></button>
        </div>
      </div>

      {body === null ? (
        <div className="muted" style={{ fontSize: "var(--fs-sm)" }}>This entry couldn't be read.</div>
      ) : quizzing && notes ? (
        <QuizView n={notes} onGoDeeper={onGoDeeper} onDone={() => setQuizzing(false)} />
      ) : (
        <div className="reader">
          <div className="reader-kicker">{kicker}</div>
          <h1>{a.title}</h1>
          <div className="reader-source">{[subject, relativeDays(a.created_at.slice(0, 10))].filter(Boolean).join(" · ")}</div>
          {body.kind === "notes" ? (
            <NotesView n={body.notes} highlights={highlights} onToggleHighlight={toggleHighlight} onGoDeeper={onGoDeeper} />
          ) : body.kind === "explanation" ? (
            <ExplanationView x={body.explanation} standalone />
          ) : (
            <PlanView p={body.plan} standalone />
          )}
        </div>
      )}

      {!quizzing && pages.length > 0 && (
        <div style={{ marginTop: "1.1rem" }}>
          <div className="sec-head"><h2>Pages you added</h2></div>
          <div className="intake-pages">
            {pages.map((p, i) => (
              <PagePhoto key={p.id} path={p.path} alt={`Page ${i + 1}`}
                onLoaded={(u) => setPageUrls((m) => (m[p.id] === u ? m : { ...m, [p.id]: u }))}
                onOpen={() => setViewing(i)} />
            ))}
          </div>
        </div>
      )}

      {!quizzing && (
        <div className="row" style={{ marginTop: "1.4rem", paddingTop: "0.9rem", borderTop: "1px solid var(--hairline)", gap: "0.5rem", flexWrap: "nowrap" }}>
          {notes && (notes.self_check?.length ?? 0) > 0 && (
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => setQuizzing(true)}>
              Quiz me on these {notes.self_check.length}
            </button>
          )}
          <button className="btn" style={{ width: 48, padding: 0 }} aria-label="Log a session" title="Log a session" onClick={onLog}>
            <img src={glyphClock} alt="" style={{ width: 17 }} />
          </button>
        </div>
      )}

      {viewing !== null && (
        <PhotoViewer
          photos={pages.map((p, i) => ({ src: pageUrls[p.id] ?? "", alt: `Page ${i + 1}` })).filter((v) => v.src) as ViewerPhoto[]}
          startIndex={Math.min(viewing, Math.max(0, pages.length - 1))}
          onClose={() => setViewing(null)}
        />
      )}
    </main>
  );
}

// A photographed page lives on disk. WKWebView will not decode it through Tauri's asset://
// scheme (it fetches, then paints a broken-image glyph), so we read the bytes over our own IPC
// and hand the webview a blob: URL. The file stays on the device; nothing transits Oriora.
function PagePhoto({ path, alt, size = 64, onLoaded, onOpen }: {
  path: string; alt: string; size?: number;
  onLoaded?: (url: string) => void;   // hand the blob URL up so the viewer can show the full image
  onOpen?: () => void;                // tap the thumbnail -> open it full-screen
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true; let made: string | null = null;
    store.readPhoto(path)
      .then((bytes) => {
        if (!live) return;
        made = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: "image/jpeg" }));
        setUrl(made); onLoaded?.(made);
      })
      .catch(() => { /* a missing page must not break the entry */ });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [path]);   // eslint-disable-line react-hooks/exhaustive-deps
  const box: React.CSSProperties = { width: size, height: Math.round(size * 1.31), borderRadius: 8, border: "1px solid var(--border)", flexShrink: 0, padding: 0, overflow: "hidden" };
  if (!url) return <div style={{ ...box, background: "var(--elev-2)" }} />;
  return (
    <button type="button" onClick={onOpen} aria-label={`Open ${alt}`} title="Tap to view"
      style={{ ...box, cursor: "pointer", background: "none", display: "block" }}>
      <img src={url} alt={alt} width={size} height={Math.round(size * 1.31)} style={{ objectFit: "cover", display: "block" }} />
    </button>
  );
}

// ── Quiz me — ACTIVE RECALL, free and on-device ─────────────────────────────────
// Every competitor ships active recall (Turbolearn, Knowt); Study had the raw material for it
// already — `self_check` questions whose answers TEACH rather than hand over an answer. So this
// costs no AI call: it re-uses what Digest produced. Answer from memory, reveal, mark it.
// The spaced-repetition SCHEDULER is deliberately NOT built here — oriora_flashcards already
// ships FSRS; a second scheduler would be a duplicate.
function QuizView({ n, onGoDeeper, onDone }: {
  n: StudyNotes;
  onGoDeeper?: (term: string) => void;
  onDone: () => void;
}) {
  const qs = n.self_check ?? [];
  const [i, setI] = useState(0);
  const [shown, setShown] = useState(false);
  const [got, setGot] = useState<boolean[]>([]);

  if (qs.length === 0) {
    return (
      <div>
        <p className="muted" style={{ fontSize: "var(--fs-sm)" }}>These notes have no self-check questions to quiz you on.</p>
        <button className="btn btn-pill" onClick={onDone}>Back to the notes</button>
      </div>
    );
  }

  if (i >= qs.length) {
    const right = got.filter(Boolean).length;
    const weak = qs.map((q, k) => (got[k] ? null : q)).filter(Boolean) as typeof qs;
    return (
      <div>
        <div style={{ fontSize: "var(--fs-md)", fontWeight: 600, marginBottom: 6 }}>
          {right} of {qs.length} from memory.
        </div>
        <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.6 }}>
          {right === qs.length
            ? "You had them all. Come back to these in a few days and try again — that's when recall sticks."
            : "The ones you missed are the ones worth another pass. Recall you had to work for is the recall that lasts."}
        </p>
        {weak.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 6, margin: "0.7rem 0" }}>
            {weak.map((q, k) => (
              <div key={k} style={{ padding: "0.5rem 0.65rem", background: "var(--elev-2)", borderRadius: 10 }}>
                <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>{q.question}</div>
                <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2, lineHeight: 1.5 }}>{q.explanation}</div>
              </div>
            ))}
          </div>
        )}
        <div className="row" style={{ gap: 8, marginTop: "0.6rem" }}>
          <button className="btn btn-pill" onClick={() => { setI(0); setShown(false); setGot([]); }}>Go again</button>
          <button className="btn btn-primary btn-pill" onClick={onDone}>Back to the notes</button>
        </div>
      </div>
    );
  }

  const q = qs[i];
  function mark(ok: boolean) {
    setGot((g) => { const c = [...g]; c[i] = ok; return c; });
    setShown(false);
    setI(i + 1);
  }
  return (
    <div>
      <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginBottom: 6 }}>Question {i + 1} of {qs.length}</div>
      <div style={{ fontSize: "var(--fs-md)", fontWeight: 600, lineHeight: 1.5, marginBottom: "0.8rem" }}>{q.question}</div>
      {!shown ? (
        <>
          <p className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: "0.7rem" }}>Answer it in your head first — that's the part that does the work.</p>
          <button className="btn btn-primary btn-pill" onClick={() => setShown(true)}>Show me</button>
        </>
      ) : (
        <>
          <div style={{ padding: "0.6rem 0.7rem", background: "var(--elev-2)", borderRadius: 10, marginBottom: "0.8rem" }}>
            <div style={{ fontSize: "var(--fs-sm)", lineHeight: 1.6 }}>{q.explanation}</div>
            {onGoDeeper && (
              <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)", marginTop: 6 }}
                onClick={() => onGoDeeper(q.question)}>Still fuzzy — explain this properly →</button>
            )}
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn-pill" onClick={() => mark(false)}>Not yet</button>
            <button className="btn btn-primary btn-pill" onClick={() => mark(true)}>Got it</button>
          </div>
        </>
      )}
    </div>
  );
}

// ── R4 — THE RITUAL: logging is the daily act; it is what makes a desk warm again ──
function RitualSheet({ subjects, defaultSubjectId, onClose, onSaved }: {
  subjects: Subject[]; defaultSubjectId: string; onClose: () => void; onSaved: (subjectId: string) => void;
}) {
  const [subjectId, setSubjectId] = useState(defaultSubjectId);
  const [studiedOn, setStudiedOn] = useState(todayLocal());
  const [preset, setPreset] = useState<number | "other">(30);
  const [otherMins, setOtherMins] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const mins = preset === "other" ? (Number(otherMins) || 0) : preset;
  const todayLabel = new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });

  async function save() {
    setSaving(true); setErr(null);
    try {
      await store.addSession({ subject_id: subjectId, studied_on: studiedOn, minutes: mins, note: note.trim() });
      onSaved(subjectId);
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <ModalShell title="What did you study?" subtitle={studiedOn === todayLocal() ? `Today, ${todayLabel}.` : undefined} onCancel={onClose}>
      <div className="row" style={{ gap: "0.35rem", marginBottom: "1rem" }}>
        {subjects.map((sub) => (
          <button key={sub.id} className={`chip toggle${subjectId === sub.id ? " selected" : ""}`} onClick={() => setSubjectId(sub.id)}>{sub.name}</button>
        ))}
      </div>

      <div className="seclabel" style={{ marginBottom: "0.5rem" }}>How long</div>
      <div className="ritual-mins" style={{ marginBottom: "0.9rem" }}>
        {[15, 30, 45, 60].map((m) => (
          <button key={m} className={preset === m ? "selected" : undefined} onClick={() => setPreset(m)}>{m === 60 ? "1h" : `${m}m`}</button>
        ))}
        <button className={preset === "other" ? "selected" : "other"} onClick={() => setPreset("other")}>other</button>
      </div>
      {preset === "other" && (
        <div className="row" style={{ gap: "0.6rem", marginBottom: "0.9rem", flexWrap: "nowrap" }}>
          <input type="number" inputMode="numeric" min={1} max={1440} placeholder="minutes" value={otherMins} onChange={(e) => setOtherMins(e.target.value)} style={{ width: 130 }} />
          <input type="date" value={studiedOn} max={todayLocal()} onChange={(e) => setStudiedOn(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
        </div>
      )}

      <input placeholder="What you worked on — optional" value={note} onChange={(e) => setNote(e.target.value)} style={{ marginBottom: "1.1rem" }} />

      {err && <div className="notice notice-err" style={{ marginBottom: "1rem" }}><AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} /><span>{err}</span></div>}
      <button className="btn btn-primary" style={{ width: "100%", minHeight: 50 }} onClick={save} disabled={saving || mins < 1 || !subjectId}>
        {saving ? "Logging…" : "Log it"}
      </button>
      <button className="btn-ghost" style={{ width: "100%", marginTop: 6 }} onClick={onClose} disabled={saving}>Cancel</button>
    </ModalShell>
  );
}

const fieldLabel: React.CSSProperties = { display: "block", fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.45rem" };

function ModalShell({ title, subtitle, children, onCancel, wide }: { title: string; subtitle?: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  // Esc closes; restore focus is browser-default. Scrim click closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="sheet-scrim" onClick={onCancel}>
      <div className="sheet sheet-center" onClick={(e) => e.stopPropagation()} style={{ width: wide ? "min(480px, calc(100% - 2rem))" : "min(400px, calc(100% - 2rem))", maxHeight: "calc(100dvh - 3rem)", overflowY: "auto", paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.1rem", gap: "0.6rem" }}>
          <div style={{ minWidth: 0 }}>
            <div className="serif" style={{ fontWeight: 600, fontSize: "1.2rem" }}>{title}</div>
            {subtitle && <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2, lineHeight: 1.4 }}>{subtitle}</div>}
          </div>
          <button className="icon-btn" aria-label="Close" onClick={onCancel} style={{ flexShrink: 0, marginTop: -2 }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
